import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Inbox, Filter } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import {
  NOTIFICATION_CATEGORY_LABEL,
  type AppNotification,
  type NotificationCategory,
} from "@/types";

const CATEGORIES: NotificationCategory[] = [
  "registration",
  "payment",
  "club",
  "progress",
  "membership",
  "system",
];

const CATEGORY_DOT: Record<NotificationCategory, string> = {
  registration: "bg-blue-500",
  payment: "bg-emerald-500",
  club: "bg-purple-500",
  progress: "bg-amber-500",
  membership: "bg-pink-500",
  system: "bg-gray-400",
};

function fmtDate(d: any): string {
  if (!d) return "";
  const date = typeof d.toDate === "function" ? d.toDate() : new Date(d);
  return date.toLocaleString("es-CO", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function NotificationsPage() {
  const { items, unread, loading, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"all" | "unread" | NotificationCategory>(
    "all",
  );

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "unread") return items.filter((n) => !n.read);
    return items.filter((n) => n.category === filter);
  }, [items, filter]);

  const handleClick = async (n: AppNotification) => {
    if (!n.read) {
      try {
        await markRead(n.id);
      } catch (err) {
        console.warn("markRead failed", err);
      }
    }
    if (n.link) navigate(n.link);
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notificaciones</h1>
            <p className="text-sm text-gray-500">
              {unread > 0 ? `${unread} sin leer` : "Todo al día"}
            </p>
          </div>
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={() => markAllRead()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-50 text-purple-700 hover:bg-purple-100 text-sm font-medium"
          >
            <CheckCheck className="w-4 h-4" />
            Marcar todas como leídas
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <span className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
          <Filter className="w-3.5 h-3.5" /> Filtrar:
        </span>
        {(
          [
            { id: "all", label: "Todas" },
            { id: "unread", label: "No leídas" },
            ...CATEGORIES.map((c) => ({
              id: c,
              label: NOTIFICATION_CATEGORY_LABEL[c],
            })),
          ] as { id: typeof filter; label: string }[]
        ).map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors",
              filter === f.id
                ? "bg-purple-600 text-white"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            Cargando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Inbox className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No hay notificaciones en este filtro</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => handleClick(n)}
                  className={cn(
                    "w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors flex gap-3 items-start",
                    !n.read && "bg-purple-50/40",
                  )}
                >
                  <span
                    className={cn(
                      "mt-2 w-2.5 h-2.5 rounded-full shrink-0",
                      CATEGORY_DOT[n.category],
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <p
                        className={cn(
                          "text-sm leading-snug truncate",
                          n.read
                            ? "text-gray-800"
                            : "text-gray-900 font-semibold",
                        )}
                      >
                        {n.title}
                      </p>
                      <span className="text-[10px] text-gray-400 shrink-0">
                        {fmtDate(n.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{n.body}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                        {NOTIFICATION_CATEGORY_LABEL[n.category]}
                      </span>
                      {n.emailStatus === "sent" && (
                        <span className="text-[10px] text-emerald-600">
                          ✉ enviado
                        </span>
                      )}
                      {n.emailStatus === "skipped" && (
                        <span
                          className="text-[10px] text-gray-400"
                          title="No se envió email por tus preferencias"
                        >
                          ✉ omitido
                        </span>
                      )}
                      {n.emailStatus === "failed" && (
                        <span
                          className="text-[10px] text-red-500"
                          title={n.emailError || ""}
                        >
                          ✉ error
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
